import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { IconProp } from "@fortawesome/fontawesome-svg-core";

import { FeatureTileDto } from "../../../../../@api";

import { useFixedLanguages } from "../../../../../utils/hooks/fixedLanguagesHook";
import { Features } from "../../../../features/feature.enum";
import { translationKeys } from "../../../../models/app-translation-keys";
import { RouteConstants } from "../../../../models/enums/route-constants.enum";
import { useStore } from "../../../../stores/StoreContext";
import { TileCard } from "../../../Shared/Components";

interface IFeatureCardProps {
  featureKey: Features;
  disabled?: boolean;
  requiresBudget?: boolean;
  featureIcon: IconProp;
  route?: RouteConstants;
  featureRoute?: RouteConstants;
  externalUrl?: string;
}

export const FeatureCard: FunctionComponent<IFeatureCardProps> = (props: IFeatureCardProps) => {
  const { budgetStore } = useStore();
  const multiTranslate = useFixedLanguages();
  const [disabled, setDisabled] = useState<boolean>(false);
  const navigate = useNavigate();

  const navigateTo = (path: string) => {
    if (props.disabled) {
      return;
    }

    const trimmed = path?.trim();
    if (!trimmed) {
      return;
    }

    const isExternal = /^https?:\/\//i.test(trimmed);

    if (isExternal) {
      // open in new tab externalLink
      window.open(trimmed, "_blank", "noopener,noreferrer");
      return;
    }

    // internal route
    const internalPath = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
    navigate(internalPath);
  };

  const concatRoute = props.route ? [props.route, props.featureRoute].join("/") : props.featureRoute;
  const targetUrl = props.externalUrl || (concatRoute ? `/${concatRoute}` : "");

  useEffect(() => {
    const isDisabled = !!props.disabled || (!!props.requiresBudget && !budgetStore.hasBudget);
    setDisabled(isDisabled);
  }, [props.disabled, props.requiresBudget, budgetStore.hasBudget]);

  const tile = useMemo<FeatureTileDto>(() => {
    const title = multiTranslate(`feature.${props.featureKey}.landing.title`);
    const description = multiTranslate(`feature.${props.featureKey}.landing.description`);
    const buttonText = multiTranslate(`feature.${props.featureKey}.landing.buttonLabel`);

    const tooltip =
      !!props.requiresBudget && !budgetStore.hasBudget
        ? multiTranslate(translationKeys.globalErrors.budgetErrors.outOfBudget, {
            remainingDays: budgetStore.remainingBudgetDays,
          })
        : props.externalUrl
          ? multiTranslate(translationKeys.pages.landingPage.tooltip.externalLinkWarning)
          : { en: "", de: "", fr: "" };

    return {
      id: props.featureKey,
      title,
      description,
      buttonText,
      tooltip,
      iconKey: props.featureIcon.toString(),
      colorText: "",
      colorBackground: "",
      colorBackgroundHover: "",
      linkUrl: {
        en: targetUrl,
        de: targetUrl,
        fr: targetUrl,
      },
      enabled: true,
      order: 0,
      showFooter: true,
    };
  }, [
    props.featureKey,
    props.requiresBudget,
    props.externalUrl,
    props.featureIcon,
    budgetStore.hasBudget,
    budgetStore.remainingBudgetDays,
    targetUrl,
    multiTranslate,
  ]);

  return (
    <TileCard
      tile={tile}
      featureIcon={props.featureIcon}
      showAdminControls={false}
      disabled={disabled}
      isExternalUrl={!!props.externalUrl}
      onClick={() => navigateTo(targetUrl || "#")}
    />
  );
};
